'use client';

import { createWithEqualityFn } from 'zustand/traditional';
import { isProductionRuntime } from '../../../lib/runtime-flags';
import type { Message } from '../chat-types';
import type { ChatSnapshot, UniversalChatContextPayload } from './types';

const noop = () => {
  // No-op placeholder for environment hooks.
};
const noopAppend: (messages: Message[]) => void = () => {
  // No-op placeholder for environment hooks.
};
const noopUpdate: (message: Message) => void = () => {
  // No-op placeholder for environment hooks.
};

type ExternalChatActions = {
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  startFresh: () => void;
  registerContext: (context: UniversalChatContextPayload) => void;
  appendMessages: (messages: Message[]) => void;
  updateMessage: (message: Message) => void;
};

interface ChatStoreState extends ExternalChatActions {
  close: () => void;
  isEnabled: boolean;
  isOpen: boolean;
  open: () => void;
  reset: () => void;
  setActions: (actions: Partial<ExternalChatActions>) => void;
  setEnabled: (enabled: boolean) => void;
  setSnapshot: (snapshot: ChatSnapshot | null) => void;
  snapshot: ChatSnapshot | null;
  toggle: () => void;
}

const defaultSnapshotState: ChatSnapshot | null = null;

export const defaultExternalActions: ExternalChatActions = {
  openChat: noop,
  closeChat: noop,
  toggleChat: noop,
  startFresh: noop,
  registerContext: noop,
  appendMessages: noopAppend,
  updateMessage: noopUpdate,
};

const initialState: Pick<ChatStoreState, 'isOpen' | 'snapshot' | 'isEnabled'> & ExternalChatActions = {
  isOpen: false,
  snapshot: defaultSnapshotState,
  isEnabled: false,
  ...defaultExternalActions,
};

export const useChatStore = createWithEqualityFn<ChatStoreState>()((set) => ({
  ...initialState,
  open: () => {
    set((state) => {
      if (state.isOpen) {
        return state;
      }
      return { ...state, isOpen: true };
    });
  },
  close: () => {
    set((state) => {
      if (!state.isOpen) {
        return state;
      }
      return { ...state, isOpen: false };
    });
  },
  toggle: () => {
    set((state) => ({
      ...state,
      isOpen: !state.isOpen,
    }));
  },
  setSnapshot: (snapshot) => {
    set((state) => {
      if (state.snapshot === snapshot) {
        return state;
      }
      return { ...state, snapshot };
    });
  },
  setEnabled: (enabled) => {
    set((state) => {
      if (state.isEnabled === enabled) {
        return state;
      }
      return { ...state, isEnabled: enabled };
    });
  },
  setActions: (actions) => {
    set((state) => ({
      // UniversalChatProvider hydrates these handlers so the hook can expose
      // stable callbacks without rerendering a React context tree.
      ...state,
      ...actions,
    }));
  },
  reset: () => {
    set(initialState);
  },
}));

export const resetChatStore = () => {
  useChatStore.setState(initialState, false);
};

declare global {
  // eslint-disable-next-line no-var
  var __SWEETISTICS_CHAT_STORE__: typeof useChatStore | undefined;
}

if (!isProductionRuntime()) {
  globalThis.__SWEETISTICS_CHAT_STORE__ = useChatStore;
}
